import React from "react"

import {
    Box,
    FormControl,
    Grid,
    MenuItem,
	SelectChangeEvent,
    Stack,
    Typography,
} from "@mui/material"
import {
	ExpandMore,
	FiberManualRecord,
} from "@mui/icons-material"

import { Trans } from "@lingui/macro"
import Chart from "chart.js/auto"

import { theme } from "index"
import { SbSelect } from "components/commons"

interface CostAnalysisChartProps
{
    chartData: {
        microjobs: number[]
        apis: number[]
        commissions: number[]
    }
}
interface CostAnalysisChartState
{
    selectedType: string[]
}
export class CostAnalysisChart extends React.Component<CostAnalysisChartProps, CostAnalysisChartState>
{
	private palette = theme.palette
	private chartRef: React.RefObject<HTMLCanvasElement> = React.createRef()
	private myChart: Chart | null = null
	private typeOptions: string [] = [
		"Tutti",
		"Microjobs",
		"Chiamate API",
		"Commissioni commerciali",
	]

	private periodOptions: string [] = [
		"Ultimo anno",
		"Ultimi 6 mesi",
		"Ultimi 3 mesi",
		"Ultimo mese",
	]
	private allLabels: string[] = [
		"Gennaio",
		"Febbraio",
		"Marzo",
		"Aprile",
		"Maggio",
		"Giugno",
		"Luglio",
		"Agosto",
		"Settembre",
		"Ottobre",
		"Novembre",
		"Dicembre",
	]

	private allDataset: {
		label: string
		data: number[]
		borderColor: string
		backgroundColor: string
		borderWidth: number
	}[] = [
		{
			label: "",
			data: this.props.chartData.microjobs,
			borderColor: this.palette.success.main,
			backgroundColor: this.palette.success.main,
			borderWidth: 2,
		},
		{
			label: "",
			data: this.props.chartData.apis,
			borderColor: this.palette.error.main,
			backgroundColor: this.palette.error.main,
			borderWidth: 2,
		},
		{
			label: "",
			data: this.props.chartData.commissions,
			borderColor: this.palette.secondary.main,
			backgroundColor: this.palette.secondary.main,
			borderWidth: 2,
		},
	]

	private selectedLabel: string [] = this.allLabels
	private selectedDataset: {
		label: string
		data: number[]
		borderColor: string
		backgroundColor: string
		borderWidth: number
	}[] = this.allDataset

	constructor(props: CostAnalysisChartProps)
	{
		super(props)
		this.state = {
			selectedType: ["Tutti"],
		}
	}

	public componentDidMount()
	{
		this.createChart()
		window.addEventListener("resize", this.handleResize)
	}

	createChart = () => {
		const canvas = this.chartRef.current
		if(canvas) {
			const ctx = canvas.getContext("2d")
			if(ctx) {
				if(this.myChart) {
					this.myChart.destroy()
				}

				this.myChart = new Chart(ctx, {
					type: "bar",
					data: {
						labels: this.selectedLabel,
						datasets: this.selectedDataset,
					},
					options: {
						aspectRatio: 0.75,
						maintainAspectRatio: false,
						plugins: {
							legend: {
								labels: {
									boxWidth: 0,
								},
							},
						},
						scales: {
							x: {
								grid: {
									display: false,
								},
							},
							y: {
								suggestedMin: 0,
								grid: {
									display: true,
								},
							},
						},
					},
				})
			}
		}
	}

	handleResize = () => {
		if(this.myChart) {
			this.myChart.resize()
		}
	}

	// private convertStringToEnumValue = (stringValue: string): DashboardContractsPeriods => {
	// 	for(const enumValue of Object.values(DashboardContractsPeriods)) {
	// 		if(stringValue === enumValue) {
	// 			return enumValue
	// 		}
	// 	}
	//
	// 	throw new Error(`The string value "${stringValue}" is not a valid enum value.`)
	// }

	private handleChangeType = (event: SelectChangeEvent<any>) => {
		const {
			target: {value},
		} = event

		this.setState(() => ({
			selectedType: typeof value === "string" ? value.split(",") : value,
		}), () => (this.renderChart()))
	}

	private renderChart = () => {
		this.selectedDataset = []

		if(this.state.selectedType.includes("Tutti") || this.state.selectedType.length === 0) {
			this.selectedDataset = this.allDataset
			this.createChart()
			return
		}
		if(this.state.selectedType.includes("Microjobs")) {
			this.selectedDataset.push(this.allDataset[0])
		}
		if(this.state.selectedType.includes("Chiamate API")) {
			this.selectedDataset.push(this.allDataset[1])
		}
		if(this.state.selectedType.includes("Commissioni commerciali")) {
			this.selectedDataset.push(this.allDataset[2])
		}
		this.createChart()
	}

	private handleChangePeriod = (event: SelectChangeEvent<any>) => {
		this.selectedLabel = []
		if(event.target.value.includes("Ultimo anno")) {
			this.selectedLabel = this.allLabels
			this.createChart()
			return
		}
		if(event.target.value.includes("Ultimi 6 mesi")) {
			this.selectedLabel = [
				"Gennaio",
				"Febbraio",
				"Marzo",
				"Aprile",
				"Maggio",
				"Giugno",
			]
		}
		if(event.target.value.includes("Ultimi 3 mesi")) {
			this.selectedLabel = [
				"Aprile",
				"Maggio",
				"Giugno",
			]
		}
		if(event.target.value.includes("Ultimo mese")) {
			this.selectedLabel = [
				"1 Giu",
				"5 Giu",
				"10 Giu",
				"15 Giu",
				"20 Giu",
				"25 Giu",
				"30 Giu",
			]
		}
		this.createChart()
	}

	render()
	{
        return (
            <Box sx={{
                backgroundColor: this.palette.background.default,
                height: "100%",
                mt: 3
            }}>
                <div>
                    <Grid container>
                        <Grid item xs={12}
                            style={{justifyContent: "right", display: 'flex', marginTop: 30, marginRight: 75}}>
                            <Stack direction="row" spacing={3}>
                                <FormControl>
                                    <SbSelect
                                        multiple
                                        variant="outlined"
                                        size="small"
                                        IconComponent={ExpandMore}
                                        value={this.state.selectedType}
                                        onChange={this.handleChangeType}>
                                        {this.typeOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </SbSelect>
                                </FormControl>
                                <FormControl>
                                    <SbSelect
                                        variant="outlined"
                                        size="small"
                                        IconComponent={ExpandMore}
                                        onChange={this.handleChangePeriod}>
                                        {this.periodOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </SbSelect>
                                </FormControl>
                            </Stack>
                        </Grid>
                        <Grid item xs={3}/>
                        <Grid item xs={2}
                            style={{justifyContent: "center", alignItems: 'center', display: 'flex', marginTop: 40}}>
                            <FiberManualRecord sx={{color: "success.main", fontSize: "3rem"}}/>
                            <Stack direction={"column"}>
                                <Typography fontWeight={500}>
                                    <Trans id="cost-analysis-chart.microjobs">
                                        Microjobs
                                    </Trans>
                                </Typography>
                                <Typography fontSize={12}>
                                    <Trans id="cost-analysis-chart.total">
                                        Totale
                                    </Trans>
                                </Typography>
                                <Typography sx={{color: "success.main"}}>
                                    {0.00}€
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={2}
                            style={{justifyContent: "center", alignItems: 'center', display: 'flex', marginTop: 40}}>
                            <FiberManualRecord sx={{color: "error.main", fontSize: "3rem"}}/>
                            <Stack direction={"column"}>
                                <Typography fontWeight={500}>
                                    <Trans id="cost-analysis-chart.apis">
                                        Chiamate API
                                    </Trans>
                                </Typography>
                                <Typography fontSize={12}>
                                    <Trans id="cost-analysis-chart.total">
                                        Totale
                                    </Trans>
                                </Typography>
                                <Typography sx={{color: "error.main"}}>
                                    {0.00}€
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={2}
                            style={{justifyContent: "center", alignItems: 'center', display: 'flex', marginTop: 40}}>
                            <FiberManualRecord sx={{color: "secondary.main", fontSize: "3rem"}}/>
                            <Stack direction={"column"}>
                                <Typography fontWeight={500}>
                                    <Trans id="cost-analysis-chart.commissions">
                                        Commissioni
                                    </Trans>
                                </Typography>
                                <Typography fontSize={12}>
                                    <Trans id="cost-analysis-chart.total">
                                        Totale
                                    </Trans>
                                </Typography>
                                <Typography sx={{color: "secondary.main"}}>
                                    {0.00}€
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={3}/>
                    </Grid>
                </div>

                <Box sx={{
                    width: '90%',
                    mx: "auto",
                    mb: 5,
                    flexGrow: 1,
                }}>
                    <canvas ref={this.chartRef}></canvas>
                </Box>
            </Box>
        )
    }
}
